import { z } from 'zod'

import { auditShape } from '@/shared/details'

export * from './paths'

const status = z.enum(['active', 'deactivated'])

/** A listed user's status: lists never show a Pending one. */
export type UserStatus = z.infer<typeof status>

const profileStatus = z.enum(['pending', 'active', 'deactivated'])

/** A user's status on their own page, Pending while the person is still being saved. */
export type ProfileStatus = z.infer<typeof profileStatus>

const pendingChangeSchema = z.object({
  kind: z.enum(['unknown', 'deactivation', 'siteMove']),
  isCanceled: z.boolean(),
  canCancel: z.boolean(),
})

/** A deactivation or move to another site still to finish, which keeps the person from signing in. */
export type PendingChange = z.infer<typeof pendingChangeSchema>

const pendingMergeSchema = z.object({
  keptId: z.string(),
  mergedId: z.string(),
  startedOn: z.string(),
  canCancel: z.boolean(),
})

/** A merge of the person with another still to finish, which keeps both from signing in or being changed. */
export type PendingMerge = z.infer<typeof pendingMergeSchema>

const userSchema = z
  .object({
    id: z.string(),
    status: profileStatus,
    isDeactivating: z.boolean().optional(),
    pendingChange: pendingChangeSchema.nullish(),
    pendingMerge: pendingMergeSchema.nullish(),
    doNotReactivate: z.boolean().optional(),
    isErased: z.boolean().optional(),
    mergedIntoId: z.string().nullish(),
    displayName: z.string(),
    organizationId: z.string(),
    organizationName: z.string(),
    managerId: z.string().nullish(),
    managerName: z.string().nullish(),
    companyId: z.string().nullish(),
    companyName: z.string().nullish(),
    externalId: z.string().nullish(),
    receivesNotifications: z.boolean(),
    participatesInAchievements: z.boolean(),
    isOutageContact: z.boolean(),
    primaryEmail: z.string().nullish(),
    otherEmails: z.array(z.string()),
    title: z.string().nullish(),
    firstName: z.string().nullish(),
    middleName: z.string().nullish(),
    lastName: z.string().nullish(),
    printedName: z.string().nullish(),
    jobTitle: z.string().nullish(),
    primaryPhone: z.string().nullish(),
    secondaryPhone: z.string().nullish(),
    addressLine1: z.string().nullish(),
    addressLine2: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    postalCode: z.string().nullish(),
    countryCode: z.string().nullish(),
    country: z.string().nullish(),
    timeZone: z.string().nullish(),
    language: z.string().nullish(),
    hasBio: z.boolean(),
    bioCompany: z.string().nullish(),
    bioTitle: z.string().nullish(),
    bioDescription: z.string().nullish(),
    linkedIn: z.string().nullish(),
    twitter: z.string().nullish(),
    birthMonth: z.number().nullish(),
    birthYear: z.number().nullish(),
    militaryVeteran: z.boolean(),
    lastSignedInOn: z.string().nullish(),
  })
  .extend(auditShape)

/** A user as User Details and Edit User show them: the profile and the person's shared details. */
export type User = z.infer<typeof userSchema>

export function parseUser(body: unknown): User {
  return userSchema.parse(body)
}

/** The message id of a profile status's label. */
export function profileStatusLabelId(value: ProfileStatus): string {
  return `siteManagement.users.status.${value}`
}

/** The base of User Details' slots: tabs, side-menu links, header actions and panels (`detailsSlots`). */
export const userDetailsSlot = 'site-management.user-details'

/** The tab slot of Create and Edit User: other features' tabs, such as roles and course credit, each saving itself. */
export const userFormTabsSlot = 'site-management.user-form.tabs'

const summarySchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string(),
  email: z.string().nullish(),
  status,
  mergedIntoId: z.string().nullish(),
  organizationId: z.string(),
  organizationName: z.string(),
  externalId: z.string().nullish(),
  managerId: z.string().nullish(),
  timeZone: z.string().nullish(),
  jobTitle: z.string().nullish(),
  primaryPhone: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  country: z.string().nullish(),
  receivesNotifications: z.boolean(),
  participatesInAchievements: z.boolean(),
  isOutageContact: z.boolean(),
  lastSignedInOn: z.string().nullish(),
  createdOn: z.string(),
  modifiedOn: z.string(),
})

/** A User Profile as Find Users and the user chooser list it. */
export type UserSummary = z.infer<typeof summarySchema>

export function parseUserSummary(item: unknown): UserSummary {
  return summarySchema.parse(item)
}

/** The message id of a status's label. */
export function statusLabelId(value: UserStatus): string {
  return `siteManagement.users.status.${value}`
}

const nameSchema = z.object({ id: z.string(), displayName: z.string() })

export function parseUserName(item: unknown): z.infer<typeof nameSchema> {
  return nameSchema.parse(item)
}
